import AxiosInstance from '@/api/axios';
import Lucide from '@/components/Base/Lucide';
import TomSelect from '@/components/Base/TomSelect';
import { IBranches } from '@/pages/Branches';
import { IRegion } from '@/pages/Settings/RegionSettingsForm';
import { updateCampaignById } from '@/stores/campaignSlice';
import { fetchChannel } from '@/stores/chatHistorySlice';
import { IClient, updateTempClient } from '@/stores/clientsSlice';
import {
  setActiveHubspotCompanyId,
  setAgents,
  setBillingBranchId,
  setBranchesExist,
  setBranchId,
  setBranchList,
  setCampaign,
  setDateRange,
  setMultipleAgents,
  setMultipleAssets,
  setOrganizationId,
  setReengaged,
  setRegionId,
  setSelectedClientTeam,
  setSelectedTeamMembers,
} from '@/stores/generalFilterSlice';
import { useAppDispatch, useAppSelector } from '@/stores/hooks';
import { fetchRegions } from '@/stores/regionSlice';
import { RootState } from '@/stores/store';
import { fetchTemplates } from '@/stores/templatesSlice';
import {
  checkSuperOwner,
  checkUser,
  checkUserAdmin,
} from '@/utils/checkAccess';
import { ROLE } from '@/utils/enum';
import { getClientId, getRoleFromLocalStorage } from '@/utils/helper';
import { ConversationAgentMap, MultiAgentMap } from '@/utils/map';
import { Template } from '@/utils/types';
import { Listbox, Transition } from '@headlessui/react';
import classNames from 'classnames';
import { format, parseISO, subDays } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import { Check, ChevronDown } from 'lucide-react';
import React, {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { DateRangePicker } from 'react-date-range';
import { useLocation, useNavigate } from 'react-router-dom';
import { twMerge } from 'tailwind-merge';
import Button from '../Base/Button';
import { FormInput, FormLabel, FormSelect } from '../Base/Form';
import { Dialog, Menu } from '../Base/Headless';
import Litepicker from '../Base/Litepicker';
import { toast } from '../Base/Toast/ToastManager';
import CampaignRules, { CampaignDayInputType } from '../Campaign/CampaignRules';
import useHubspotCompanyOptions from '../Hubspot/useFetchCompanies';
import { AgentSettings } from '../TemplatesPanel';
import { setActiveClient } from '@/stores/activeClientSlice';
import encryptDecrypt from '@/utils/encryptDecrypt';
import { setAllowReEngage, updateMenu } from '@/stores/sideMenuSlice';
import clsx from 'clsx';
import userFallback from '@/assets/images/users/icon-user.jpg';
import { createPortal } from 'react-dom';

interface DropdownVenuesProps {
  id?: string;
  setBranchValue?: (value: string) => void;
  className?: string;
  readonly?: boolean;
  withLabel?: boolean;
  allowOther?: boolean;
}

interface DropdownRegionsProps {
  id?: string;
  setRegionValue?: (value: string) => void;
  className?: string;
  readonly?: boolean;
  withLabel?: boolean;
}

interface IBranchSelectedFilter {
  branch_id?: string;
}

type campaignType = {
  _id: string;
  name: string;
};

function DropdownVenues({
  id,
  setBranchValue,
  className,
  withLabel,
  readonly = false,
  allowOther,
}: DropdownVenuesProps) {
  const dispatch = useAppDispatch();
  const selectedBranch = useAppSelector(
    (state: RootState) => state.generalFilterSlice.branch_id
  );
  const [uploadSelectedBranch, setUploadSelectedBranch] = useState<
    string | null
  >();

  const handleBranchChange = (value: string) => {
    if (readonly) {
      return;
    }
    if (setBranchValue) {
      setBranchValue(value);
      setUploadSelectedBranch(value);
    } else {
      dispatch(setBranchId(value));
    }
  };
  const [branches, setBranches] = useState<
    | [
      {
        _id: string;
        name: string;
      },
    ]
    | null
  >();
  const client = getClientId();

  const checkBranches = (branches: IBranches[]) => {
    const exists = branches.some((item) => item._id === selectedBranch);
    if (exists) {
      return true;
    } else {
      return false;
    }
  };

  const generalFilter = useAppSelector(
    (state: RootState) => state.generalFilterSlice
  );

  useEffect(() => {
    const fetchBranches = async () => {
      const response = await AxiosInstance.get(`/branches/get-all/${client}`);
      setBranches(response.data.data);
      dispatch(setBranchList(response.data.data));

      if (
        Array.isArray(response.data.data) &&
        response.data.data.length === 0
      ) {
        dispatch(setBranchesExist(false));
      } else {
        dispatch(setBranchesExist(true));
      }

      if (selectedBranch && selectedBranch === 'all') {
        dispatch(setBranchId(response.data.data[0]._id));
      }
      if (response.data?.data) {
        if (!checkBranches(response.data.data)) {
          dispatch(setBranchId(response.data.data[0]._id));
        }
      }
    };
    fetchBranches();
  }, [client, generalFilter.branchCreated, generalFilter.branchesExist]);

  useEffect(() => {
    if (selectedBranch) {
      setUploadSelectedBranch(selectedBranch);
    }
  }, [selectedBranch]);

  return (
    <div className='flex flex-col gap-2 md:flex-row md:items-center md:gap-8 bg-white'>
      {branches && branches.length > 0 && (
        <>
          {withLabel && (
            <FormLabel className='min-w-[12rem]'>Select a Location</FormLabel>
          )}
          <div className='w-full relative'>
            <div className='w-full flex-1 xl:mt-0'>
              <div
                className={
                  withLabel
                    ? 'w-full'
                    : 'flex flex-col items-center md:flex-row capitalize'
                }
              >
                <TomSelect
                  name='branches'
                  id={id ? id : 'venue-filter'}
                  value={
                    setBranchValue
                      ? uploadSelectedBranch
                        ? uploadSelectedBranch
                        : 'all'
                      : selectedBranch || 'all'
                  }
                  onChange={(e: { target: { value: string } }) => {
                    if (e.target.value === '') return;
                    handleBranchChange(e.target.value);
                  }}
                  options={{
                    maxOptions: 500,
                  }}
                  className={
                    className
                      ? className
                      : `group-[.mode--light]:!border-transparent group-[.mode--light]:!bg-white/[0.12] group-[.mode--light]:bg-chevron-white group-[.mode--light]:text-white dark:text-white w-full min-w-[18.5rem]  rounded-md text-sm`
                  }
                >
                  {allowOther && (
                    <option value='others' className='text-primary'>
                      All Locations
                    </option>
                  )}
                  <optgroup id='opt-location-group' label='Location'>
                    {branches?.map((branch, index) => (
                      <option
                        key={index}
                        value={branch._id}
                        className='text-primary'
                      >
                        {branch.name}
                      </option>
                    ))}
                  </optgroup>
                </TomSelect>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function DropdownRegions({
  id,
  setRegionValue,
  className,
  withLabel,
  readonly = false,
}: DropdownRegionsProps) {
  const dispatch = useAppDispatch();
  const selectedRegion = useAppSelector(
    (state: RootState) => state.generalFilterSlice.region_id
  );
  const [uploadSelectedRegion, setUploadSelectedRegion] = useState<
    string | null
  >();

  const handleRegionChange = (value: string) => {
    if (readonly) {
      return;
    }
    if (setRegionValue) {
      setRegionValue(value);
      setUploadSelectedRegion(value);
    } else {
      dispatch(setRegionId(value));
    }
  };
  const [regions, setRegions] = useState<
    | [
      {
        _id: string;
        name: string;
      },
    ]
    | null
  >();

  const clientId = getClientId();

  const checkRegions = (regions: IRegion[]) => {
    const exists = regions.some((item) => item._id === selectedRegion);
    if (exists) {
      return true;
    } else {
      return false;
    }
  };

  useEffect(() => {
    const fetchRegions = async () => {
      const response = await AxiosInstance.get(`/region/${clientId}`);
      setRegions(response.data.data);

      if (selectedRegion && selectedRegion === 'all') {
        dispatch(setRegionId(response.data.data[0]._id));
      }
      if (response.data?.data) {
        if (!checkRegions(response.data.data)) {
          dispatch(setRegionId(response.data.data[0]._id));
        }
      }
    };
    fetchRegions();
  }, [clientId]);

  useEffect(() => {
    if (selectedRegion) {
      setUploadSelectedRegion(selectedRegion);
    }
  }, [selectedRegion]);

  return (
    <div className='flex items-center gap-8'>
      {regions && regions.length > 0 && (
        <>
          {withLabel && (
            <FormLabel className='min-w-[12rem]'>Select a Region</FormLabel>
          )}
          <div className='w-full relative'>
            <div className='w-full flex-1 xl:mt-0'>
              <div
                className={
                  withLabel
                    ? 'w-full'
                    : 'flex flex-col items-center md:flex-row capitalize'
                }
              >
                <TomSelect
                  name='branches'
                  id={id ? id : 'region-filter'}
                  value={
                    setRegionValue
                      ? uploadSelectedRegion
                        ? uploadSelectedRegion
                        : 'all'
                      : selectedRegion || 'all'
                  }
                  onChange={(e: { target: { value: string } }) => {
                    if (e.target.value === '') return;
                    handleRegionChange(e.target.value);
                  }}
                  className={
                    className
                      ? className
                      : `bg-white group-[.mode--light]:!border-transparent group-[.mode--light]:!bg-white/[0.12] group-[.mode--light]:bg-chevron-white group-[.mode--light]:text-white dark:text-white rounded-md text-sm uppercase w-full min-w-[18.5rem]`
                  }
                >
                  <optgroup
                    id='opt-region-group'
                    label='Region/Brand'
                    className='capitalize'
                  >
                    {regions?.map((region, index) => (
                      <option
                        key={index}
                        value={region._id}
                        className='text-primary uppercase'
                      >
                        {region.name}
                      </option>
                    ))}
                  </optgroup>
                </TomSelect>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function DropdownRegionAndBranch({
  id,
  className = '',
  withLabel,
  allowAll,
  setLocationFilter,
  showCaption = false,
  loading = false,
  data,
  theme = 'light',
  defaultToAll = false,
  preserveReengagedOnInit = false,
}: {
  id?: string;
  className?: string;
  withLabel?: boolean;
  allowAll?: boolean;
  setLocationFilter?: Function;
  showCaption?: boolean;
  loading?: boolean;
  data?: any;
  theme?: 'dark' | 'light';
  defaultToAll?: boolean;
  preserveReengagedOnInit?: boolean;
}) {
  const location = useLocation();
  const client = useAppSelector((state: RootState) => state.activeClient.id);

  type IBranch = { _id: string; name: string };
  type IRegion = { _id: string; name: string };
  type ISelectedFilter = { region_id: string; branch_id: string };

  const [branches, setBranches] = useState<IBranch[] | null>(null);

  const { region } = useAppSelector((state: RootState) => state.regions);
  const generalFilter = useAppSelector(
    (state: RootState) => state.generalFilterSlice
  );

  const [selectedFilter, setSelectedFilter] = useState<ISelectedFilter>({
    region_id: generalFilter.region_id || '',
    branch_id: generalFilter.branch_id || '',
  });
  const dispatch = useAppDispatch();
  const isUserOwnerAdmin = checkUser() || checkUserAdmin();

  // Ensures defaultToAll only forces the "All Locations" default once per
  // mount, ahead of any saved Redux filter, without fighting a user's
  // manual selection afterward (the main effect can re-run when the
  // region/branches array references change).
  const hasAppliedDefaultToAll = useRef(false);

  const setBranch = (branch_id: string) => {
    setSelectedFilter({
      branch_id: branch_id,
      region_id: '',
    });
    if (setLocationFilter)
      setLocationFilter({
        branch_id: branch_id,
        region_id: '',
      });
    dispatch(setBranchId(branch_id));
  };

  const setRegion = (region_id: string) => {
    setSelectedFilter({
      branch_id: '',
      region_id: region_id,
    });
    if (setLocationFilter)
      setLocationFilter({
        branch_id: '',
        region_id: region_id,
      });
    dispatch(setRegionId(region_id));
  };

  const shouldClearReengaged = (isAutomatic: boolean) =>
    !(isAutomatic && preserveReengagedOnInit);

  const handleRegionChange = (region_id: string, isAutomatic = false) => {
    setRegion(region_id);
    if (shouldClearReengaged(isAutomatic)) dispatch(setReengaged(''));
  };

  const handleBranchChange = (branch_id: string, isAutomatic = false) => {
    setBranch(branch_id);
    if (shouldClearReengaged(isAutomatic)) dispatch(setReengaged(''));
  };

  useEffect(() => {
    const fetchBranches = async () => {
      const response = await AxiosInstance.get(`/branches/get-all/${client}`);
      setBranches(response.data.data);
      dispatch(setBranchList(response.data.data));

      const branchData = response.data.data;
      if (Array.isArray(branchData) && branchData.length === 0) {
        dispatch(setBranchesExist(false));
      } else {
        dispatch(setBranchesExist(true));
      }
    };
    fetchBranches();
  }, [client, generalFilter.branchCreated]);

  useEffect(() => {
    if (client) dispatch(fetchRegions({ client }));
  }, [client]);

  useEffect(() => {
    if (!region || !branches) return;
    const path = location.pathname;
    const regionList = Array.isArray(region) ? region : [];
    const branchList = Array.isArray(branches) ? branches : [];

    if (path.includes('/account') && branchList.length) {
      if (branchList.length < 2 && isUserOwnerAdmin) {
        handleBranchChange(generalFilter.branch_id || branchList[0]._id, true);
      } else {
        handleBranchChange('others', true);
      }
      return;
    }

    if (
      defaultToAll &&
      allowAll &&
      !hasAppliedDefaultToAll.current &&
      !(branchList.length < 2 && isUserOwnerAdmin)
    ) {
      hasAppliedDefaultToAll.current = true;
      handleBranchChange('others', true);
      return;
    }

    if (
      allowAll &&
      (generalFilter.branch_id === 'others' || generalFilter.branch_id === 'all')
    ) {
      handleBranchChange('others', true);
      return;
    }

    if (client === generalFilter.organization_id) {
      if (
        generalFilter.region_id &&
        regionList.some((r) => r._id === generalFilter.region_id)
      ) {
        handleRegionChange(generalFilter.region_id, true);
        return;
      }
      if (
        generalFilter.branch_id &&
        branchList.some((b) => b._id === generalFilter.branch_id)
      ) {
        handleBranchChange(generalFilter.branch_id, true);
        return;
      }
    }

    if (regionList.length > 0 && branchList.length >= 2) {
      handleRegionChange(regionList[0]._id, true);
    } else if (branchList.length > 0) {
      handleBranchChange(branchList[0]._id, true);
    }
  }, [allowAll, region, branches, defaultToAll]);

  return (
    <div className='z-50 flex flex-start flex-col md:flex-row md:items-center gap-3'>
      {Array.isArray(region) &&
        region.length &&
        Array.isArray(branches) &&
        branches.length ? (
        <>
          <div
            className={`flex flex-col gap-1 ${theme === 'light' ? '' : 'text-white'} ${id && 'w-full'} ${theme === 'light' ? 'relative' : ''}`}
          >
            {showCaption && (
              <p className='text-white hidden lg:block'>
                Select a Region/Brand or Location
              </p>
            )}
            {withLabel && (
              <FormLabel className='min-w-[16rem] hidden lg:block'>
                Select a Region/Brand or Location
              </FormLabel>
            )}
            {theme === 'light' && (
              <Lucide
                icon='MapPin'
                className='pointer-events-none absolute inset-y-0 left-3 z-10 my-auto h-4 w-4 stroke-[1.5] text-slate-400'
              />
            )}
            <TomSelect
              id={id ? id : 'combined-controlCenter-filter'}
              value={
                selectedFilter.region_id
                  ? `region-${selectedFilter.region_id}`
                  : selectedFilter.branch_id
                    ? `branch-${selectedFilter.branch_id}`
                    : ''
              }
              name='regions-and-branches'
              onChange={(e: { target: { value: string } }) => {
                const value = e.target.value;
                if (!value) return;
                if (value.startsWith('region-')) {
                  handleRegionChange(value.replace('region-', ''));
                } else if (value.startsWith('branch-')) {
                  handleBranchChange(value.replace('branch-', ''));
                }
              }}
              className={`z-50 cursor-pointer w-full min-w-[18.5rem] capitalize rounded-lg text-sm border ${theme === 'light'
                ? `bg-white border-slate-200 shadow-sm [&_.ts-control]:!h-10 [&_.ts-control]:!rounded-lg [&_.ts-control]:!border-none  [&_.ts-control]:!text-slate-700 [&_.ts-control_input]:!text-slate-700`
                : `group-[.mode--light]:!border-transparent group-[.mode--light]:!bg-white/[0.12] group-[.mode--light]:bg-chevron-white group-[.mode--light]:!text-[#30F291] ${selectedFilter.region_id || selectedFilter.branch_id
                  ? '!text-[#30F291]'
                  : 'text-white border-white/50'
                }`
                } ${className} ${data === null ? 'opacity-50 pointer-events-none' : ''}`}
              options={{ placeholder: 'Select a Region or Location...' }}
            >
              {allowAll && !(branches.length < 2 && isUserOwnerAdmin) && (
                <option value='branch-others' className='text-primary'>
                  All Locations
                </option>
              )}
              {Array.isArray(region) &&
                region.length &&
                Array.isArray(branches) &&
                !(branches.length < 2 && isUserOwnerAdmin) && (
                  <optgroup
                    id='opt-region-group'
                    label='Region or Brand'
                    className='bg-success !uppercase'
                  >
                    {region.map((region: IRegion, index: number) => (
                      <option
                        key={index}
                        value={`region-${region._id}`}
                        className='ml-10 uppercase '
                      >
                        {region.name.toUpperCase()}
                      </option>
                    ))}
                  </optgroup>
                )}

              {/* Branches Optgroup */}
              {branches && Array.isArray(branches) && branches.length && (
                <optgroup label='Locations'>
                  {branches.map((branch, index) => (
                    <option
                      key={index}
                      value={`branch-${branch?._id}`}
                      className='text-primary'
                    >
                      {branch?.name}
                    </option>
                  ))}
                </optgroup>
              )}
            </TomSelect>
          </div>
        </>
      ) : (
        <></>
      )}
    </div>
  );
}

export interface IPlatformActiveValue {
  web: boolean;
  facebook: boolean;
  email: boolean;
  whatsapp: boolean;
  instagram: boolean;
  sms: boolean;
  form: boolean;
  phone: boolean;
  viber: boolean;
}

function DropdownChannels({
  loading = false,
  data,
}: {
  loading?: boolean;
  data?: any;
}) {
  const dispatch = useAppDispatch();
  const selectedAgent = useAppSelector(
    (state: RootState) => state.generalFilterSlice.agent
  );
  const generalFilter = useAppSelector(
    (state: RootState) => state.generalFilterSlice
  );

  const client = getClientId();

  useEffect(() => {
    if (client) {
      dispatch(fetchChannel(client));
    }
  }, []);

  const { packageAccess } = useAppSelector(
    (state: RootState) => state.visitorHistory
  );

  const [platformActiveValue, setPlatformActiveValue] =
    useState<IPlatformActiveValue>({
      web: false,
      facebook: false,
      email: false,
      whatsapp: false,
      instagram: false,
      sms: false,
      form: false,
      phone: false,
      viber: false,
    });

  const handleAgentChange = (value: string) => {
    dispatch(setAgents(value));
  };

  useEffect(() => {
    async function fetchPlatformSettings() {
      if (
        !generalFilter.region_id &&
        (!generalFilter.branch_id || generalFilter.branch_id === 'all')
      )
        return;
      try {
        let params;

        if (generalFilter.region_id) {
          params = {
            regionId: generalFilter.region_id,
          };
        } else {
          params = {
            branchId: generalFilter.branch_id,
          };
        }
        // setLoading(true);
        const response = await AxiosInstance.get(`/platformSetting/${client}`, {
          params,
        });
        setPlatformActiveValue({
          web:
            response.data.data.find((item: any) => item.channel === 'web')
              ?.active || false,
          facebook:
            response.data.data.find((item: any) => item.channel === 'facebook')
              ?.active || false,
          email:
            response.data.data.find((item: any) => item.channel === 'email')
              ?.active || false,
          whatsapp:
            response.data.data.find((item: any) => item.channel === 'whatsapp')
              ?.active || false,
          instagram:
            response.data.data.find((item: any) => item.channel === 'facebook')
              ?.active || false,
          sms:
            response.data.data.find((item: any) => item.channel === 'sms')
              ?.active || false,
          form:
            response.data.data.find((item: any) => item.channel === 'form')
              ?.active || false,
          phone:
            response.data.data.find((item: any) => item.channel === 'phone')
              ?.active || false,
          viber:
            response.data.data.find((item: any) => item.channel === 'viber')
              ?.active || false,
        });
      } catch (error) {
        console.error('Error fetching platform settings:', error);
      }
    }
    fetchPlatformSettings();
  }, [generalFilter]);

  const filteredAgentMap =
    client === '675683526e9ec2644acfa98d'
      ? ConversationAgentMap
      : packageAccess
        ? ConversationAgentMap.filter((agent) => {
          if (agent.agent === 'facebook' || agent.agent === 'instagram') {
            return packageAccess.social;
          }
          if (
            agent.agent === 'outboundcall' ||
            agent.agent === 'inboundcall' ||
            agent.agent === 'webcall'
          ) {
            // webcall belongs with the other two: without an arm here it
            // would fall through to packageAccess['webcall'], which does not
            // exist on PackageAccessType, and be hidden from every tenant.
            return packageAccess.voice;
          }
          // Viber has no packageAccess entry of its own yet (see
          // billing.service.ts). Gate it on WhatsApp for now: both are
          // provider-brokered messaging channels and Viber is rolling out
          // alongside WhatsApp. Give Viber its own plan flag when
          // packaging decides how to price it.
          if (agent.agent === 'viber') {
            return packageAccess.whatsapp;
          }
          return (packageAccess as any)[agent.agent];
        })
        : [];

  return (
    <div className='relative'>
      <div className='w-full flex-1 xl:mt-0'>
        <div className='flex flex-col items-center md:flex-row capitalize'>
          <TomSelect
            name='agent'
            id='channel-filter'
            value={selectedAgent || 'all'}
            onChange={(e) => {
              if (e.target.value === '') return;
              if (!loading) {
                handleAgentChange(e.target.value as string);
              }
            }}
            options={{ placeholder: 'Search' }}
            className={`bg-white w-full group-[.mode--light]:!border-transparent group-[.mode--light]:!bg-white/[0.12] group-[.mode--light]:bg-chevron-white group-[.mode--light]:text-slate-200 sm:w-44 ${data === null ? 'opacity-50 pointer-events-none' : ''}`}
          >
            <option value='all'>All Channels</option>
            {filteredAgentMap.map((agentItem, index) => {
              return (
                <option
                  key={index}
                  value={agentItem.agent}
                  className='uppercase'
                >
                  {agentItem.view}
                </option>
              );
            })}
          </TomSelect>
        </div>
      </div>
    </div>
  );
}

function DropdownMultiChannels({
  loading = false,
  data,
  selectedMetric,
  theme = 'dark',
}: {
  loading?: boolean;
  data?: any;
  selectedMetric: 'summary' | 'sales';
  theme?: 'dark' | 'light';
}) {
  const dispatch = useAppDispatch();
  const selectedAgents = useAppSelector(
    (state: RootState) => state.generalFilterSlice.agentMultiple
  ) as string[];

  const selectedReengaged = useAppSelector(
    (state: RootState) => state.generalFilterSlice.reengaged
  ) as string[];

  const { packageAccess } = useAppSelector(
    (state: RootState) => state.visitorHistory
  );

  const { branch_id: selectedBranch, region_id: selectedRegion } =
    useAppSelector((state: RootState) => state.generalFilterSlice);

  const client = getClientId();

  useEffect(() => {
    if (client) {
      dispatch(fetchChannel(client));
    }
  }, []);

  const handleAgentChange = (values: string[]) => {
    dispatch(setMultipleAgents(values));
  };

  const handleReengagedChange = (values: string[]) => {
    dispatch(setReengaged(values));
  };

  const filteredAgentMap =
    client === '675683526e9ec2644acfa98d'
      ? MultiAgentMap
      : packageAccess
        ? MultiAgentMap.filter((agent) => {
          if (agent.agent === 'facebook' || agent.agent === 'instagram') {
            return packageAccess.social;
          }
          if (agent.agent === 'outboundcall') {
            return packageAccess.voice;
          }
          return (packageAccess as any)[agent.agent];
        })
        : [];

  const toggleSelection = (agent: string) => {
    if (selectedAgents.includes(agent)) {
      handleAgentChange(selectedAgents.filter((a) => a !== agent));
    } else {
      handleAgentChange([...selectedAgents, agent]);
    }
  };

  const toggleReengagedSelection = (reengaged: string) => {
    if (selectedReengaged.includes(reengaged)) {
      handleReengagedChange(selectedReengaged.filter((r) => r !== reengaged));
    } else {
      handleReengagedChange([...selectedReengaged, reengaged]);
    }
  };

  const [reengageOptions, setReengageOptions] = useState<campaignType[]>([]);

  const hasSelection =
    selectedMetric === 'summary'
      ? selectedAgents.length > 0 || selectedReengaged.length > 0
      : selectedReengaged.length > 0;

  const buttonText = () => {
    if (selectedMetric === 'sales') {
      return selectedReengaged.length > 0
        ? `${selectedReengaged.length} Campaign(s)`
        : 'Select Campaign(s)';
    }
    if (hasSelection) {
      let parts: any = [];
      if (selectedAgents.length > 0)
        parts.push(`${selectedAgents.length} channel(s)`);
      if (selectedReengaged.length > 0)
        parts.push(`${selectedReengaged.length} campaign(s)`);
      return parts.join(', ');
    }
    return 'Select Channels and Campaigns';
  };

  useEffect(() => {
    const fetchCampaigns = async () => {
      try {
        const response = await AxiosInstance.get(
          `/re-engage/${client}/list/campaign?region=${selectedRegion || ''}&branch=${selectedBranch || ''}`
        );
        if (response.data && response.data?.data?.length > 0) {
          setReengageOptions(response.data.data);
        }
      } catch (error) {
        console.error('[Error:frontend] fetchCampaigns', error);
      }
    };
    fetchCampaigns();
  }, [selectedRegion, selectedBranch]);

  return (
    <div className='relative z-50 w-full sm:w-auto'>
      <Listbox value={selectedAgents} onChange={handleAgentChange} multiple>
        <div className='relative'>
          <Listbox.Button
            className={classNames(
              'relative h-10 w-full sm:w-auto cursor-pointer rounded-lg text-sm capitalize',
              theme === 'light'
                ? 'border border-slate-200 bg-white pl-9 pr-8 text-slate-700 shadow-sm'
                : 'h-[38px] px-3 pr-10 group-[.mode--light]:!border-transparent group-[.mode--light]:!bg-white/[0.12] text-[#d7e3ef] border-white/50',
              data === null ? 'opacity-50 pointer-events-none' : ''
            )}
          >
            {theme === 'light' && (
              <Lucide
                icon='SlidersHorizontal'
                className='pointer-events-none absolute inset-y-0 left-3 my-auto h-4 w-4 stroke-[1.5] text-slate-400'
              />
            )}
            <span className='block truncate'>{buttonText()}</span>
            <span className='pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2'>
              <ChevronDown
                className={twMerge(
                  'h-4 w-4',
                  theme === 'light' ? 'text-slate-400' : 'text-current'
                )}
              />
            </span>
          </Listbox.Button>

          <Transition
            as={Fragment}
            leave='transition ease-in duration-100'
            leaveFrom='opacity-100'
            leaveTo='opacity-0'
          >
            <Listbox.Options className='absolute z-[1000] mt-1 max-h-60 w-full overflow-auto rounded-md bg-white text-base shadow-lg ring-1 ring-black/5 focus:outline-none sm:text-sm'>
              {selectedMetric === 'summary' && (
                <>
                  <li className='px-4 py-2 text-xs font-semibold text-gray-500 uppercase bg-gray-100'>
                    Channels
                  </li>
                  {filteredAgentMap.map((agentItem, index) => (
                    <Listbox.Option
                      key={`channel-${index}`}
                      value={agentItem.agent}
                      as={Fragment}
                    >
                      {({ active }) => (
                        <li
                          onClick={() => {
                            if (selectedReengaged.length > 0) return;
                            toggleSelection(agentItem.agent);
                          }}
                          className={classNames(
                            'flex items-center justify-between px-4 py-2 cursor-pointer',
                            active ? 'bg-gray-100' : '',
                            selectedReengaged.length > 0 &&
                            'opacity-50 cursor-not-allowed pointer-events-none'
                          )}
                        >
                          <div className='flex items-center gap-2'>
                            <input
                              type='checkbox'
                              checked={selectedAgents.includes(agentItem.agent)}
                              readOnly
                              disabled={selectedReengaged.length > 0}
                              className='cursor-not-allowed'
                            />
                            <span className='uppercase text-sm text-gray-800'>
                              {agentItem.view}
                            </span>
                          </div>
                          {selectedAgents.includes(agentItem.agent) && (
                            <Check className='h-4 w-4 text-green-500' />
                          )}
                        </li>
                      )}
                    </Listbox.Option>
                  ))}
                </>
              )}
              <li className='px-4 py-2 text-xs font-semibold text-gray-500 uppercase bg-gray-100'>
                Campaigns
              </li>
              {reengageOptions.map((reengaged) => (
                <li
                  key={`reengaged-${reengaged._id}`}
                  onClick={() => toggleReengagedSelection(reengaged._id)}
                  className='flex items-center justify-between px-4 py-2 cursor-pointer hover:bg-gray-100'
                >
                  <div className='flex items-center gap-2'>
                    <input
                      type='checkbox'
                      checked={selectedReengaged.includes(reengaged._id)}
                      readOnly
                    />
                    <span className='uppercase text-sm text-gray-800'>
                      {reengaged?.name}
                    </span>
                  </div>
                  {selectedReengaged.includes(reengaged?._id) && (
                    <Check className='h-4 w-4 text-green-500' />
                  )}
                </li>
              ))}
            </Listbox.Options>
          </Transition>
        </div>
      </Listbox>
    </div>
  );
}

function DropdownAssets({
  loading = false,
  data,
}: {
  loading?: boolean;
  data?: any;
}) {
  return (
    <div className='relative'>
      <div className='w-full flex-1 xl:mt-0'>
        <div className='flex flex-col items-center md:flex-row capitalize'>
          <TomSelect
            name='agent'
            id='asset-filter'
            value='Select Assets'
            onChange={(e) => {
              if (!loading) {
                console.log(e.target.value);
              }
            }}
            options={{ placeholder: 'Select Assets' }}
            className={`w-full group-[.mode--light]:!border-transparent group-[.mode--light]:!bg-white/[0.12] group-[.mode--light]:bg-chevron-white group-[.mode--light]:text-slate-200 sm:w-44 ${data === null ? 'opacity-50 pointer-events-none' : ''}`}
          >
            <option key='paid' value='paid' className='uppercase'>
              Paid
            </option>
            <option key='organic' value='organic' className='uppercase'>
              Organic
            </option>
          </TomSelect>
        </div>
      </div>
    </div>
  );
}

function DropdownDateRange() {
  const dispatch = useAppDispatch();
  const selectedDateRange = useAppSelector(
    (state: RootState) => state.generalFilterSlice.date_range
  );

  const handleDateRangeChange = (value: string) => {
    dispatch(setDateRange(value));
  };

  return (
    <div className='relative'>
      <Lucide
        icon='Calendar'
        className='absolute inset-y-0 left-0 z-10 my-auto ml-3 h-4 w-4 stroke-[1.3] group-[.mode--light]:!text-slate-200'
      />
      <Litepicker
        name='dateRange'
        value={selectedDateRange}
        onChange={(e) => {
          handleDateRangeChange(e.target.value);
        }}
        options={{
          autoApply: false,
          singleMode: false,
          numberOfColumns: 2,
          numberOfMonths: 2,
          showWeekNumbers: true,
          dropdowns: {
            minYear: 1990,
            maxYear: null,
            months: true,
            years: true,
          },
        }}
        className="block disabled:bg-slate-100 disabled:cursor-not-allowed dark:disabled:bg-darkmode-800/50 dark:disabled:border-transparent [&[readonly]]:bg-slate-100 [&[readonly]]:cursor-not-allowed [&[readonly]]:dark:bg-darkmode-800/50 [&[readonly]]:dark:border-transparent transition duration-200 ease-in-out text-sm border-slate-200 shadow-sm placeholder:text-slate-400/90 focus:ring-4 focus:ring-primary focus:ring-opacity-20 focus:border-primary focus:border-opacity-40 dark:bg-darkmode-800 dark:border-transparent dark:focus:ring-slate-700 dark:focus:ring-opacity-50 dark:placeholder:text-slate-500/80 [&[type=\'file\']]:border file:mr-4 file:py-2 file:px-4 file:rounded-l-md file:border-0 file:border-r-[1px] file:border-slate-100/10 file:text-sm file:font-semibold file:bg-slate-100 file:text-slate-500/70 hover:file:bg-200 group-[.form-inline]:flex-1 group-[.input-group]:rounded-none group-[.input-group]:[&:not(:first-child)]:border-l-transparent group-[.input-group]:first:rounded-l group-[.input-group]:last:rounded-r group-[.input-group]:z-10 datepicker rounded-[0.3rem] pl-9 group-[.mode--light]:!border-transparent group-[.mode--light]:!bg-white/[0.12] group-[.mode--light]:!text-slate-200 md:w-64 mx-auto w-full "
      />
    </div>
  );
}

function DropdownClients() {
  const dispatch = useAppDispatch();
  const [selectedClient, setSelectedClient] = useState<string>('-');
  const [clients, setClients] = useState<IClient[] | []>([]);

  useEffect(() => {
    const fetchClients = async () => {
      try {
        const response = await AxiosInstance.get(`/clients?perpage=999`);
        if (response.data.success) {
          setClients(response.data.data.data);
          if (
            Array.isArray(response.data.data?.data) &&
            response.data.data?.data.length > 0
          ) {
            setSelectedClient(response.data.data.data[0]._id);
          } else {
            setSelectedClient('');
          }
        }
      } catch (error) {
        console.log(error);
      }
    };
    fetchClients();
  }, []);

  useEffect(() => {
    const clientData = clients.find((client) => client._id === selectedClient);
    dispatch(updateTempClient(clientData || { name: 'All Customers' }));
    return () => {
      dispatch(updateTempClient(null));
    };
  }, [selectedClient]);

  return (
    <div>
      {/* DropDownClients */}
      <TomSelect
        name='clients-filter'
        value={selectedClient}
        className='order-1 z-50 cursor-pointer min-w-[12rem] w-full w-max-[20rem] capitalize group-[.mode--light]:!border-transparent group-[.mode--light]:!bg-white/[0.12] group-[.mode--light]:bg-chevron-white group-[.mode--light]:!text-[#30F291] sm:w-44 rounded-md text-sm'
        onChange={(e: { target: { value: string } }) => {
          setSelectedClient(e.target.value);
        }}
        options={{
          allowEmptyOption: true,
        }}
      >
        <option value='' className='text-white'>
          All Customers
        </option>
        {Array.isArray(clients) &&
          clients.length &&
          clients.map((client) => (
            <option key={client._id} value={client._id}>
              {client.name}
            </option>
          ))}
      </TomSelect>
    </div>
  );
}

interface DropdownRect {
  top: number;
  left: number;
  width: number;
}

export function SidebarDropdownClient() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const activeClientId = useAppSelector((state) => state.activeClient.id);

  const [clients, setClients] = useState<IClient[]>([]);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [rect, setRect] = useState<DropdownRect | null>(null);

  const triggerRef = useRef<HTMLButtonElement | null>(null);

  const popupRef = useRef<HTMLDivElement | null>(null);

  /**
   * Fetch clients
   */
  useEffect(() => {
    const fetchClients = async () => {
      try {
        const response = await AxiosInstance.get('/clients?perpage=999');

        if (response.data.success) {
          const list: IClient[] = response.data.data.data ?? [];

          setClients(list);
        }
      } catch (error) {
        console.log(error);
      }
    };

    fetchClients();
  }, []);

  /**
   * Selected client
   */
  const selectedClientId = activeClientId ?? clients[0]?._id ?? '';

  const selectedClientData = clients.find(
    (client) => client._id === selectedClientId
  );

  const selectedLabel = selectedClientData?.name ?? 'Select client';

  /**
   * Filter clients
   */
  const filteredClients = useMemo(() => {
    if (!search.trim()) {
      return clients;
    }

    const query = search.trim().toLowerCase();

    return clients.filter((client) =>
      client.name?.toLowerCase().includes(query)
    );
  }, [clients, search]);

  /**
   * Position dropdown
   */
  const updateRect = useCallback(() => {
    const element = triggerRef.current;

    if (!element) return;

    const bounds = element.getBoundingClientRect();

    setRect({
      top: bounds.bottom + 6,
      left: bounds.left,
      width: bounds.width,
    });
  }, []);

  /**
   * Dropdown events
   */
  useEffect(() => {
    if (!open) return;

    updateRect();

    const handleReposition = () => {
      updateRect();
    };

    window.addEventListener('scroll', handleReposition, true);

    window.addEventListener('resize', handleReposition);

    const handleOutside = (event: MouseEvent) => {
      const target = event.target as Node;

      if (
        triggerRef.current &&
        !triggerRef.current.contains(target) &&
        popupRef.current &&
        !popupRef.current.contains(target)
      ) {
        setOpen(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handleOutside);

    document.addEventListener('keydown', handleEscape);

    return () => {
      window.removeEventListener('scroll', handleReposition, true);

      window.removeEventListener('resize', handleReposition);

      document.removeEventListener('mousedown', handleOutside);

      document.removeEventListener('keydown', handleEscape);
    };
  }, [open, updateRect]);

  /**
   * Clear search when dropdown closes
   */
  useEffect(() => {
    if (!open) {
      setSearch('');
    }
  }, [open]);

  /**
   * Activate client
   */
  const activateClient = useCallback(
    (
      client: IClient,
      options?: {
        navigateTo?: boolean;
      }
    ) => {
      dispatch(
        setActiveClient({
          id: client._id,
          name: client.name,
          profile: client.media?.path || null,
          salesReport: client.salesReport,
          reEngage: client.reEngage ?? false,
        })
      );

      const encryptedId = encryptDecrypt.encryptClientId(client._id);

      dispatch(updateMenu(encryptedId));

      dispatch(setAllowReEngage(client.reEngage ?? false));

      dispatch(setOrganizationId(client._id));

      dispatch(setBranchId(''));
      dispatch(setRegionId(''));

      if (options?.navigateTo) {
        navigate(`/${encryptedId}/`);
      }
    },
    [dispatch, navigate]
  );

  /**
   * Select client
   */
  const handleSelect = useCallback(
    (clientId: string) => {
      const client = clients.find((item) => item._id === clientId);

      if (!client) return;

      if (clientId === selectedClientId) {
        setOpen(false);
        return;
      }

      setOpen(false);

      activateClient(client, {
        navigateTo: true,
      });
    },
    [clients, selectedClientId, activateClient]
  );

  /**
   * Default to first client if
   * there is no valid active client
   */
  useEffect(() => {
    if (clients.length === 0) return;

    const currentClientId = activeClientId || getClientId();

    const currentClientExists =
      !!currentClientId &&
      clients.some((client) => client._id === currentClientId);

    if (currentClientExists) return;

    const firstClient = clients[0];

    if (!firstClient) return;

    activateClient(firstClient);
  }, [clients, activeClientId, activateClient]);

  return (
    <div className='w-full'>
      <button
        type='button'
        ref={triggerRef}
        onClick={() => setOpen((value) => !value)}
        className={clsx([
          'order-1 z-50 flex w-full max-w-[20rem] min-w-[12rem] items-center justify-between gap-2 sm:w-52',
          'cursor-pointer rounded-md border border-transparent bg-white/[0.12] py-2',
          'text-[13px] font-semibold capitalize transition hover:bg-white/[0.18]',
        ])}
      >
        <img
          src={selectedClientData?.media?.path || userFallback}
          className='h-8 w-8 flex-none rounded-lg'
          alt={selectedLabel}
        />

        {/* Active client name */}
        <span className='min-w-0 flex-1 truncate text-left'>
          {selectedLabel}
        </span>

        {/* Arrow */}
        <span
          className={clsx([
            'ml-auto flex h-7 w-7 flex-none items-center justify-center',
            'rounded-md text-slate-500 transition-all duration-200',

            open ? 'bg-white/[0.15]' : 'bg-transparent hover:bg-white/[0.12]',
          ])}
        >
          <Lucide
            icon='ChevronDown'
            className={clsx([
              'h-5 w-5',
              'stroke-[2.5]',
              'transition-transform duration-200 ease-in-out',

              open && 'rotate-180',
            ])}
          />
        </span>
      </button>

      {open &&
        rect &&
        createPortal(
          <div
            ref={popupRef}
            style={{
              position: 'fixed',
              top: rect.top,
              left: rect.left,
              width: Math.max(rect.width, 260),
              zIndex: 9999,
            }}
            className={clsx([
              'overflow-hidden',
              'rounded-lg',
              'border',
              'border-slate-200',
              'bg-white',
              'text-sm',
              'text-slate-700',
              'shadow-xl',

              'dark:border-slate-700',
              'dark:bg-slate-800',
              'dark:text-slate-100',
            ])}
          >
            <div
              className={clsx([
                'border-b',
                'border-slate-100',
                'bg-white',
                'p-3',

                'dark:border-slate-700',
                'dark:bg-slate-800',
              ])}
            >
              <div className='relative'>
                <Lucide
                  icon='Search'
                  className='pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400'
                />

                <input
                  autoFocus
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder='Search clients...'
                  className={clsx([
                    'h-10',
                    'w-full',
                    'rounded-md',
                    'border',
                    'border-slate-200',
                    'bg-slate-50',
                    'pl-9',
                    'pr-9',
                    'text-[13px]',
                    'text-slate-700',
                    'outline-none',
                    'transition',

                    'placeholder:text-slate-400',

                    'focus:border-slate-300',
                    'focus:bg-white',
                    'focus:ring-2',
                    'focus:ring-slate-200',

                    'dark:border-slate-700',
                    'dark:bg-slate-900/40',
                    'dark:text-slate-100',
                    'dark:focus:bg-slate-900',
                    'dark:focus:ring-slate-700',
                  ])}
                />

                {search && (
                  <button
                    type='button'
                    onClick={() => setSearch('')}
                    className='absolute right-2 top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded text-slate-400 transition hover:bg-slate-200 hover:text-slate-600 dark:hover:bg-slate-700'
                  >
                    <Lucide icon='X' className='h-3.5 w-3.5' />
                  </button>
                )}
              </div>
            </div>

            <div className='max-h-[280px] overflow-y-auto p-1.5'>
              {filteredClients.length === 0 ? (
                <div className='px-3 py-8 text-center'>
                  <div className='mb-1 text-xs font-medium text-slate-500 dark:text-slate-300'>
                    No clients found
                  </div>

                  <div className='truncate text-[11px] text-slate-400'>
                    No results for "{search}"
                  </div>
                </div>
              ) : (
                filteredClients.map((client) => {
                  const isSelected = selectedClientId === client._id;

                  return (
                    <button
                      key={client._id}
                      type='button'
                      onClick={() => handleSelect(client._id)}
                      className={clsx([
                        'group',
                        'flex',
                        'w-full',
                        'min-w-0',
                        'items-center',
                        'gap-2.5',
                        'rounded-md',
                        'px-2',
                        'py-2',
                        'text-left',
                        'capitalize',
                        'transition-colors',

                        isSelected
                          ? ['bg-slate-100', 'font-medium', 'dark:bg-slate-700']
                          : ['hover:bg-slate-100', 'dark:hover:bg-slate-700'],
                      ])}
                    >
                      {/* Client image */}

                      <img
                        src={client.media?.path || userFallback}
                        alt={client.name}
                        className='h-8 w-8 flex-none rounded-lg'
                      />

                      {/* Client name
                          This will never wrap */}

                      <span className='min-w-0 flex-1 truncate'>
                        {client.name}
                      </span>

                      {/* Selected state */}

                      {isSelected && (
                        <Lucide
                          icon='Check'
                          className='h-4 w-4 flex-none text-slate-500 dark:text-slate-300'
                        />
                      )}
                    </button>
                  );
                })
              )}
            </div>
          </div>,
          document.body
        )}
    </div>
  );
}

function DropdownClientTeams({
  customClass,
  roles,
}: {
  customClass: string;
  roles?: string[];
}) {
  const dispatch = useAppDispatch();
  const { selectedClientTeam } = useAppSelector(
    (state) => state.generalFilterSlice
  );
  const [clientTeams, setClientTeams] = useState<IClient[] | []>([]);
  const client = getClientId();
  const role = getRoleFromLocalStorage();
  const [team, setTeam] = useState<Map<string, IClient[]>>(
    new Map([
      [ROLE.SUPEROWNER, []],
      [ROLE.USERKEEPME, []],
      [ROLE.KEEPME, []],
      [ROLE.OWNER, []],
      [ROLE.ADMIN, []],
      [ROLE.USER, []],
    ])
  );

  const fetchSuperowners = useCallback(async () => {
    if (role === ROLE.SUPEROWNER) {
      try {
        const response = await AxiosInstance.get('/client-teams/super/list');
        return response.data;
      } catch (error) {
        console.error('Error fetching superowners:', error);
        return [];
      }
    }
    return null;
  }, [role]);

  const fetchClientTeam = useCallback(async () => {
    try {
      const response = await AxiosInstance.get(
        `/client-teams/${client}?page=1&perpage=999`
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching client teams:', error);
      return [];
    }
  }, [client]);

  useEffect(() => {
    const fetchTeam = async () => {
      try {
        const [superTeam, clientTeam] = await Promise.all([
          fetchSuperowners(),
          fetchClientTeam(),
        ]);

        let teams: any[] = [];

        // Push clientTeam data
        if (clientTeam && clientTeam.success) {
          const clientTeamsData = clientTeam.data.data;
          teams = teams.concat(clientTeamsData);
        }

        // Push superTeam data
        if (superTeam && superTeam.success) {
          teams = [...superTeam.team, ...teams];
        }

        // Set default based on roles filter
        const firstVisible = roles
          ? teams.find((t) => roles.includes(t.role))
          : teams[0];

        dispatch(setSelectedClientTeam(firstVisible?._id ?? ''));

        const roleMap = new Map<string, IClient[]>([
          [ROLE.SUPEROWNER, []],
          [ROLE.USERKEEPME, []],
          [ROLE.KEEPME, []],
          [ROLE.OWNER, []],
          [ROLE.ADMIN, []],
          [ROLE.USER, []],
        ]);

        teams.forEach((member) => {
          if (roleMap.has(member.role)) {
            roleMap.get(member.role)?.push(member);
          }
        });
        setTeam(roleMap);
        setClientTeams(teams);
      } catch (error) {
        console.error('Error in fetching teams:', error);
      }
    };

    fetchTeam();
  }, [client, role, dispatch, fetchSuperowners, fetchClientTeam]);

  return (
    <div>
      <TomSelect
        name='client-team-filter'
        id='client-team-filter'
        value={selectedClientTeam || ''}
        className={twMerge(
          'order-1 z-50 cursor-pointer min-w-[16rem] w-full max-w-[20] rounded-md text-sm text-primary group-[.mode--light]:!border-dark/10 group-[.mode--light]:!bg-light/[0.12] group-[.mode--light]:bg-chevron-dark group-[.mode--light]:text-black',
          customClass
        )}
        onChange={(e: { target: { value: string } }) => {
          dispatch(setSelectedClientTeam(e.target.value));
        }}
      >
        {Array.from(team)
          .filter(([role]) => !roles || roles.includes(role))
          .map(([role, clients]) => {
            if (Array.isArray(clients) && clients.length > 0) {
              return (
                <optgroup key={role} label={role}>
                  {clients.map((client) => (
                    <option key={client._id} value={client._id}>
                      {client.email}
                    </option>
                  ))}
                </optgroup>
              );
            }
            return null;
          })}
      </TomSelect>
    </div>
  );
}
export interface SelectionRange {
  startDate: Date;
  endDate: Date;
  key: string;
}
const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

function DropdownDateRangeUpdate({
  allowClear = false,
  startCleared = false,
}: {
  allowClear?: boolean;
  startCleared?: boolean;
}) {
  const dispatch = useAppDispatch();

  const selectedDateRange = useAppSelector(
    (state: RootState) => state.generalFilterSlice.month_date_range
  );

  const [showDatePicker, setShowDatePicker] = useState(false);

  // Default range used whenever there's no value to work with — either on
  // first mount before Redux has anything, or after the user clears it.
  const getDefaultRange = (): SelectionRange => ({
    startDate: toZonedTime(subDays(new Date(), 31), timeZone),
    endDate: toZonedTime(new Date(), timeZone),
    key: 'selection',
  });

  const initialRange: SelectionRange = selectedDateRange
    ? {
      startDate: toZonedTime(
        parseISO(selectedDateRange.split(' ')[0]),
        timeZone
      ),
      endDate: toZonedTime(
        parseISO(selectedDateRange.split(' ')[2]),
        timeZone
      ),
      key: 'selection',
    }
    : getDefaultRange();

  const [state, setState] = useState<SelectionRange[]>([initialRange]);

  // Purely local/session flag: true only while the user has explicitly
  // cleared the filter via the X button. It does NOT get set just because
  // selectedDateRange happens to be undefined on mount — that case is
  // instead auto-filled with the 31-day default (see effects below).
  const [isCleared, setIsCleared] = useState(startCleared && !selectedDateRange);
  
  const dropdownRef = useRef<HTMLDivElement>(null);

  const formatDateLocal = (date: Date): string => {
    const zonedDate = toZonedTime(date, timeZone);
    return format(zonedDate, 'yyyy-MM-dd');
  };

  // Sync FROM Redux -> local state, when a real value is present.
  useEffect(() => {
    if (!selectedDateRange) {
      // Nothing to sync in — either never set, or was just cleared.
      // Local `state` already holds a sensible range (the default, or
      // whatever was last picked); leave it alone here. The sync-to-Redux
      // effect below will repopulate Redux with that default unless the
      // absence is due to an intentional clear (isCleared).
      return;
    }

    const dateRangeParts = selectedDateRange.split(' ');
    if (!dateRangeParts || dateRangeParts.length < 3) {
      // Handle malformed date range string
      console.error(
        'Malformed date range string from Redux:',
        selectedDateRange
      );
      return;
    }

    const startDate = parseISO(dateRangeParts[0]);
    const endDate = parseISO(dateRangeParts[2]);

    setState([
      {
        startDate: toZonedTime(startDate, timeZone),
        endDate: toZonedTime(endDate, timeZone),
        key: 'selection',
      },
    ]);
    // A real value showed up in Redux (e.g. set elsewhere) — no longer cleared.
    setIsCleared(false);
  }, [selectedDateRange]); // Dependency ensures this runs if selectedDateRange changes

  // Sync TO Redux <- local state.
  useEffect(() => {
    // While explicitly cleared, don't auto-dispatch the local calendar's
    // default range back into Redux — that would silently undo the clear.
    if (isCleared) return;
    if (!state.length || !state[0].startDate || !state[0].endDate) {
      // If state is not properly populated, do nothing.
      return;
    }
    const start = formatDateLocal(state[0].startDate);
    const end = formatDateLocal(state[0].endDate);
    const dateRange = `${start} - ${end}`;

    // Only dispatch if the new range is different from the current Redux state
    // to prevent infinite loops if the Redux state update triggers this effect.
    // This also covers the initial-mount case: if selectedDateRange is
    // undefined, `dateRange` (from the 31-day default) will differ from it,
    // so the default gets dispatched into Redux automatically here.
    if (dateRange !== selectedDateRange) {
      console.log("dispatching setDateRange with", dateRange);
      dispatch(setDateRange(dateRange));
    }
  }, [state, dispatch, selectedDateRange, isCleared]);

  // Close picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setShowDatePicker(false);
      }
    };

    if (showDatePicker) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDatePicker]);

  const handleRangeChange = (item: any) => {
    setIsCleared(false);
    setState([item.selection as any]);
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsCleared(true);
    setState([getDefaultRange()]); // reset the calendar's own UI to something sensible for next time it's opened
    dispatch(setDateRange(undefined));
    setShowDatePicker(false);
  };

  const displayDateRange = isCleared
    ? ''
    : state.length && state[0].startDate && state[0].endDate
      ? `${formatDateLocal(state[0].startDate)} - ${formatDateLocal(state[0].endDate)}`
      : selectedDateRange || ''; // Fallback to Redux state or empty string

  return (
    <div className='relative' ref={dropdownRef}>
      <div
        className='flex items-center gap-8'
        onClick={() => setShowDatePicker((prev) => !prev)}
      >
        <Lucide
          icon='Calendar'
          className='absolute cursor-pointer inset-y-0 left-0 z-10 my-auto ml-3 h-4 w-4 stroke-[1.3] group-[.mode--light]:!text-slate-200'
        />
        <FormInput
          value={displayDateRange}
          placeholder='Select date range...'
          className="caret-transparent block disabled:bg-white-100 disabled:cursor-pointer dark:disabled:bg-darkmode-800/50 dark:disabled:border-transparent [&[readonly]]:bg-slate-100 [&[readonly]]:cursor-not-allowed [&[readonly]]:dark:bg-darkmode-800/50 [&[readonly]]:dark:border-transparent transition duration-200 ease-in-out text-sm border-slate-200 shadow-sm placeholder:text-slate-400/90 focus:ring-4 focus:ring-primary focus:ring-opacity-20 focus:border-primary focus:border-opacity-40 dark:bg-darkmode-800 dark:border-transparent dark:focus:ring-slate-700 dark:focus:ring-opacity-50 dark:placeholder:text-slate-500/80 [&[type=\'file\']]:border file:mr-4 file:py-2 file:px-4 file:rounded-l-md file:border-0 file:border-r-[1px] file:border-slate-100/10 file:text-sm file:font-semibold file:bg-slate-100 file:text-slate-500/70 hover:file:bg-200 group-[.form-inline]:flex-1 group-[.input-group]:rounded-none group-[.input-group]:[&:not(:first-child)]:border-l-transparent group-[.input-group]:first:rounded-l group-[.input-group]:last:rounded-r group-[.input-group]:z-10 !h-10 !rounded-lg pl-9 group-[.mode--light]:!border-transparent group-[.mode--light]:!bg-white/[0.12] group-[.mode--light]:!text-slate-200 md:w-64 mx-auto w-full cursor-pointer"
        />
        {allowClear && !isCleared && (
          <Lucide
            icon='X'
            onClick={handleClear}
            className='absolute cursor-pointer inset-y-0 right-0 z-10 my-auto mr-3 h-4 w-4 stroke-[1.3] text-slate-400 hover:text-slate-600 group-[.mode--light]:!text-slate-200'
          />
        )}
      </div>

      {showDatePicker && (
        <div className='rounded-md overflow-hidden'>
          <DateRangePicker
            onChange={handleRangeChange}
            moveRangeOnFirstSelection={false}
            months={2}
            ranges={state}
            direction='horizontal'
            className='absolute right-0 top-12 z-10 transition-all ease-in-out'
          />
        </div>
      )}
    </div>
  );
}

function DropdownBranch({
  id,
  className = '',
  withLabel,
  allowAll,
  showCaption = false,
  loading = false,
  data,
  defaultFirst = false,
}: {
  id?: string;
  className?: string;
  withLabel?: boolean;
  allowAll?: boolean;
  setLocationFilter?: Function;
  showCaption?: boolean;
  loading?: boolean;
  data?: any;
  defaultFirst?: boolean;
}) {
  const client = getClientId();
  const [branches, setBranches] = useState<
    | [
      {
        _id: string;
        name: string;
      },
    ]
    | null
  >();
  const isUserOwnerAdmin = checkUser() || checkUserAdmin();

  const [selectedFilter, setSelectedFilter] = useState<IBranchSelectedFilter>({
    branch_id: '',
  });
  const dispatch = useAppDispatch();

  const setBranch = (branch_id: string) => {
    setSelectedFilter({
      branch_id: branch_id,
    });
    dispatch(setBillingBranchId(branch_id));
  };

  const handleBranchChange = (branch_id: string) => {
    setBranch(branch_id);
  };

  useEffect(() => {
    const fetchBranches = async () => {
      const response = await AxiosInstance.get(`/branches/get-all/${client}`);
      setBranches(response.data.data);
      dispatch(setBranchList(response.data.data));
      if (response.data.data.length < 2 && isUserOwnerAdmin) {
        setBranch(response.data.data[0]._id);
      }
      if (defaultFirst && response.data.data.length > 0) {
        setBranch(response.data.data[0]._id);
      }
    };
    fetchBranches();
  }, [client, allowAll]);

  useEffect(() => {
    if (allowAll) {
      setBranch('');
    }
  }, []);

  return (
    <div className='z-50 flex flex-start flex-col md:flex-row md:items-center gap-3 bg-white'>
      {Array.isArray(branches) && branches.length && (
        <>
          {showCaption && (
            <p className='hidden md:block text-white'>Select a Location</p>
          )}
          {withLabel && (
            <FormLabel className='hidden md:block min-w-[16rem]'>
              Select a Location
            </FormLabel>
          )}

          <div className={`flex flex-col gap-1 text-white ${id && 'w-full'}`}>
            <TomSelect
              id={id ? id : 'combined-controlCenter-filter'}
              value={
                selectedFilter.branch_id
                  ? `branch-${selectedFilter.branch_id}`
                  : 'branch-others'
              }
              name='regions-and-branches'
              onChange={(e: { target: { value: string } }) => {
                const value = e.target.value;

                if (!value) return;
                if (value.startsWith('branch-')) {
                  handleBranchChange(value.replace('branch-', ''));
                }
              }}
              className={`z-50 cursor-pointer w-full min-w-[18.5rem] capitalize group-[.mode--light]:!border-transparent group-[.mode--light]:!bg-white/[0.12] group-[.mode--light]:bg-chevron-white group-[.mode--light]:!text-[#30F291] rounded-md text-sm ${selectedFilter.branch_id
                  ? '!text-[#30F291]'
                  : 'text-white border-white/50'
                } ${className} ${data === null ? 'opacity-50 pointer-events-none' : ''}`}
              options={{ placeholder: 'Select Location...' }}
            >
              {allowAll && !(branches.length < 2 && isUserOwnerAdmin) && (
                <option value='branch-others' className='text-primary'>
                  All Locations
                </option>
              )}
              {/* Branches Optgroup */}
              {branches &&
                Array.isArray(branches) &&
                branches.length &&
                branches.map((branch, index) => (
                  <option
                    key={index}
                    value={`branch-${branch?._id}`}
                    className='text-primary'
                  >
                    {branch?.name}
                  </option>
                ))}
            </TomSelect>
          </div>
        </>
      )}
    </div>
  );
}

function DropdownTeamMembers({
  data,
  className,
  teamMembersData = [],
}: {
  data?: any;
  className?: string;
  teamMembersData?: any[];
}) {
  const dispatch = useAppDispatch();
  const selectedMembers = useAppSelector(
    (state: RootState) => state.generalFilterSlice.selectedTeamMembers
  );

  const [teamMembers, setTeamMembers] = useState<any[]>([]);
  const client = getClientId();

  useEffect(() => {
    const fetchTeamMembers = async () => {
      if (teamMembersData && teamMembersData.length > 0) {
        setTeamMembers(teamMembersData);
        return;
      }
      if (!client) return;
      try {
        const response = await AxiosInstance.get(
          `/client-teams/report-recipients/${client}`
        );
        if (response.data.success) {
          setTeamMembers(response.data.data);
        }
      } catch (error) {
        console.error('Error fetching team members:', error);
      }
    };

    fetchTeamMembers();
  }, [client]);

  const handleSelection = (selectedItems: any[]) => {
    dispatch(setSelectedTeamMembers(selectedItems));
  };

  return (
    <div className='relative w-full'>
      <Listbox
        value={selectedMembers}
        onChange={handleSelection}
        multiple
        by='_id'
      >
        <div className='relative'>
          <Listbox.Button
            className={
              className
                ? `${className}`
                : classNames(
                  'relative w-full cursor-default rounded-md border bg-transparent py-2 pl-3 pr-10 text-left text-sm',
                  'group-[.mode--light]:border-transparent group-[.mode--light]:bg-white/[0.12] group-[.mode--light]:text-white',
                  data === null ? 'opacity-50 pointer-events-none' : ''
                )
            }
          >
            <span className='block truncate capitalize'>
              {selectedMembers && selectedMembers.length > 0
                ? `${selectedMembers.length} team member selected`
                : 'Select Team Members'}
            </span>
            <span className='pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2'>
              <ChevronDown className='h-5 w-5' />
            </span>
          </Listbox.Button>

          <Transition
            as={Fragment}
            leave='transition ease-in duration-100'
            leaveFrom='opacity-100'
            leaveTo='opacity-0'
          >
            <Listbox.Options className='absolute z-[1000] mt-1 max-h-60 w-full overflow-auto rounded-md bg-white text-base shadow-lg ring-1 ring-black/5 focus:outline-none sm:text-sm'>
              {teamMembers.map((member) => (
                <Listbox.Option
                  key={member._id}
                  value={member}
                  className={({ active }) =>
                    `relative cursor-default select-none py-2 pl-10 pr-4 ${active ? 'bg-primary/10 text-primary' : 'text-gray-900'
                    }`
                  }
                >
                  {({ selected }) => (
                    <>
                      <span
                        className={`block truncate capitalize ${selected ? 'font-medium' : 'font-normal'
                          }`}
                      >
                        {member.name}
                      </span>
                      {selected ? (
                        <span className='absolute inset-y-0 left-0 flex items-center pl-3 text-primary'>
                          <Check className='h-5 w-5' aria-hidden='true' />
                        </span>
                      ) : null}
                    </>
                  )}
                </Listbox.Option>
              ))}
            </Listbox.Options>
          </Transition>
        </div>
      </Listbox>
    </div>
  );
}

export default DropdownDateRangeUpdate;

const MemoizedDropdownVenues = React.memo(DropdownVenues);
const MemoizedDropdownRegions = React.memo(DropdownRegions);
const MemoizedDropdownClients = React.memo(DropdownClients);

function DropdownBranchMapping({
  onChange,
  onRegionChange,
  onAllLocationsChange,
  showAllLocations = false,
  value = [],
}: {
  onChange: (selectedBranchIds: string[]) => void;
  onRegionChange?: (regionId: string) => void;
  onAllLocationsChange?: (isAllLocations: boolean) => void;
  showAllLocations?: boolean;
  value?: string[];
  theme?: 'dark' | 'light';
}) {
  const client = getClientId();
  const dispatch = useAppDispatch();
  const [branchMappings, setBranchMappings] = useState<any[]>([]);
  const [allBranches, setAllBranches] = useState<any[]>([]);
  const [selected, setSelected] = useState<string[]>(value);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRegion, setSelectedRegion] = useState<string>('');

  const { region: regionList } = useAppSelector(
    (state: RootState) => state.regions
  );

  useEffect(() => {
    if (client) dispatch(fetchRegions({ client }));
  }, [client, dispatch]);

  useEffect(() => {
    const fetchBranchMappings = async () => {
      try {
        const res = await AxiosInstance.get(`/branch-mapping/${client}`);
        setBranchMappings(res.data.data || []);
      } catch (err) {
        console.error('Error fetching branch mappings:', err);
      }
    };
    fetchBranchMappings();
  }, [client]);

  // Fetch all branches
  useEffect(() => {
    const fetchAllBranches = async () => {
      try {
        const res = await AxiosInstance.get(`/branches/get-all/${client}`);
        setAllBranches(res.data.data || []);
      } catch (err) {
        console.error('Error fetching branches:', err);
      }
    };
    fetchAllBranches();
  }, [client]);

  // Update selected when value prop changes
  useEffect(() => {
    setSelected(value || []);
  }, [value]);

  const groupedOptions = useMemo(() => {
    const grouped: Record<string, any[]> = {};

    // Fallback: group by branch mappings
    branchMappings.forEach((mapping) => {
      grouped[mapping.mapName] = mapping.branches || [];
    });

    // Find unmapped branches
    const mappedBranchIds = new Set<string>();
    branchMappings.forEach((mapping) => {
      (mapping.branches || []).forEach((branch: any) => {
        mappedBranchIds.add(branch._id);
      });
    });

    const unmappedBranches = allBranches.filter(
      (branch) => !mappedBranchIds.has(branch._id)
    );

    if (unmappedBranches.length > 0) {
      grouped['Other'] = unmappedBranches;
    }

    return grouped;
  }, [branchMappings, allBranches]);

  const handleChange = (values: string[]) => {
    setSelected(values);
    onChange(values);
  };

  const handleRegionSelect = (regionId: string) => {
    if (onAllLocationsChange) onAllLocationsChange(false);
    if (selectedRegion === regionId) {
      // Deselect region
      setSelectedRegion('');
      handleChange([]);
      if (onRegionChange) onRegionChange('');
      return;
    }
    setSelectedRegion(regionId);
    // Clear branch mapping selection when a region is selected
    handleChange([]);
    if (onRegionChange) onRegionChange(regionId);
  };

  const handleToggleGroup = (mapName: string) => {
    // Clear region selection when branch mapping is used
    setSelectedRegion('');
    if (onAllLocationsChange) onAllLocationsChange(false);
    if (onRegionChange) onRegionChange('');

    const branchIds = groupedOptions[mapName].map((b) => b._id);
    const isAllSelected = branchIds.every((id) => selected.includes(id));

    let updated = isAllSelected
      ? selected.filter((id) => !branchIds.includes(id))
      : [...selected, ...branchIds.filter((id) => !selected.includes(id))];

    handleChange(updated);
  };

  const handleToggleItem = (id: string) => {
    // Clear region selection when branch mapping is used
    setSelectedRegion('');
    if (onAllLocationsChange) onAllLocationsChange(false);
    if (onRegionChange) onRegionChange('');

    const updated = selected.includes(id)
      ? selected.filter((v) => v !== id)
      : [...selected, id];

    handleChange(updated);
  };

  const handleAllLocationsSelect = () => {
    setSelectedRegion('');
    handleChange([]);
    if (onRegionChange) onRegionChange('');
    if (onAllLocationsChange) onAllLocationsChange(true);
  };

  const filteredOptions = useMemo(() => {
    if (!searchTerm.trim()) return groupedOptions;

    const lowerTerm = searchTerm.toLowerCase();
    const filtered: Record<string, any[]> = {};

    Object.entries(groupedOptions).forEach(([mapName, branchList]) => {
      const matching = branchList.filter((b) =>
        `${mapName} ${b.name}`.toLowerCase().includes(lowerTerm)
      );

      if (matching.length > 0) filtered[mapName] = matching;
    });

    return filtered;
  }, [groupedOptions, searchTerm]);

  const filteredRegions = useMemo(() => {
    if (!searchTerm.trim()) return regionList || [];
    const lowerTerm = searchTerm.toLowerCase();
    return (regionList || []).filter((r: any) =>
      r.name.toLowerCase().includes(lowerTerm)
    );
  }, [regionList, searchTerm]);

  const displayLabel = useMemo(() => {
    if (showAllLocations && selected.length === 0 && !selectedRegion) {
      return 'All locations';
    }
    if (selectedRegion) {
      const region = (regionList || []).find(
        (r: any) => r._id === selectedRegion
      );
      return region ? region.name : 'Select Filter';
    }
    if (selected.length > 0) {
      return `${selected.length} Location(s)`;
    }
    return 'Select Filter';
  }, [showAllLocations, selectedRegion, selected, regionList]);

  return (
    <Listbox value={selected} onChange={handleChange} multiple>
      <div className='relative'>
        <Listbox.Button
          className={classNames(
            'relative h-10 w-full sm:w-auto cursor-pointer rounded-lg text-sm capitalize',
            'border border-slate-200 bg-white pl-4 pr-8 text-slate-700 shadow-sm'
          )}
        >
          <span className='block truncate'>{displayLabel}</span>
          <span className='pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2'>
            <ChevronDown className='h-5 w-5 text-gray-500' />
          </span>
        </Listbox.Button>

        <Transition
          as={Fragment}
          leave='transition ease-in duration-100'
          leaveFrom='opacity-100'
          leaveTo='opacity-0'
        >
          <Listbox.Options className='absolute z-50 mt-1 max-h-72 w-full overflow-auto rounded-md bg-white text-base shadow-lg ring-1 ring-black/5 focus:outline-none sm:text-sm'>
            <div className='p-2 border-b'>
              <input
                type='text'
                placeholder='Search...'
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className='w-full border rounded px-2 py-1 text-sm border-primary/20'
              />
            </div>

            {showAllLocations && (
              <li
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleAllLocationsSelect();
                }}
                className={classNames(
                  'flex items-center justify-between px-4 py-2 cursor-pointer uppercase',
                  !selectedRegion && selected.length === 0
                    ? 'bg-blue-50'
                    : 'hover:bg-gray-100'
                )}
              >
                <div className='flex items-center gap-2'>
                  <input
                    type='radio'
                    checked={!selectedRegion && selected.length === 0}
                    readOnly
                  />
                  <span className='text-sm text-gray-800'>All locations</span>
                </div>
                {!selectedRegion && selected.length === 0 && (
                  <Check className='h-4 w-4 text-green-500' />
                )}
              </li>
            )}

            {/* Regions section */}
            {Array.isArray(filteredRegions) && filteredRegions.length > 0 && (
              <>
                <div className='px-4 py-1 text-xs font-bold text-gray-400 uppercase tracking-wide bg-gray-50'>
                  Region/Brand
                </div>
                {filteredRegions.map((r: any) => (
                  <li
                    key={r._id}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleRegionSelect(r._id);
                    }}
                    className={classNames(
                      'flex items-center justify-between px-4 py-2 cursor-pointer uppercase',
                      selectedRegion === r._id
                        ? 'bg-blue-50'
                        : 'hover:bg-gray-100'
                    )}
                  >
                    <div className='flex items-center gap-2'>
                      <input
                        type='radio'
                        checked={selectedRegion === r._id}
                        readOnly
                      />
                      <span className='text-sm text-gray-800'>{r.name}</span>
                    </div>
                    {selectedRegion === r._id && (
                      <Check className='h-4 w-4 text-green-500' />
                    )}
                  </li>
                ))}
              </>
            )}

            {Object.keys(filteredOptions).length > 0 && (
              <div className='px-4 py-1 text-xs font-bold text-gray-400 uppercase tracking-wide bg-gray-50'>
                Branch Mappings
              </div>
            )}
            {Object.entries(filteredOptions).map(([mapName, branchList]) => {
              if (branchList.length === 0) return null;

              const branchIds = branchList.map((b) => b._id);
              const groupSelected = branchIds.every((id) =>
                selected.includes(id)
              );

              return (
                <div key={mapName}>
                  <div
                    onClick={() => handleToggleGroup(mapName)}
                    className='px-4 py-2 bg-gray-100 font-semibold cursor-pointer hover:bg-gray-200 flex items-center gap-2'
                  >
                    <input type='checkbox' checked={groupSelected} readOnly />
                    <span>{mapName}</span>
                  </div>

                  {branchList.map((branch) => (
                    <Listbox.Option
                      key={branch._id}
                      value={branch._id}
                      as={Fragment}
                    >
                      {({ active }) => (
                        <li
                          onClick={() => handleToggleItem(branch._id)}
                          className={classNames(
                            'pl-8 flex items-center justify-between px-4 py-2 cursor-pointer',
                            active ? 'bg-gray-100' : ''
                          )}
                        >
                          <div className='flex items-center gap-2'>
                            <input
                              type='checkbox'
                              checked={selected.includes(branch._id)}
                              readOnly
                            />
                            <span className='text-sm text-gray-800'>
                              {branch.name}
                            </span>
                          </div>
                          {selected.includes(branch._id) && (
                            <Check className='h-4 w-4 text-green-500' />
                          )}
                        </li>
                      )}
                    </Listbox.Option>
                  ))}
                </div>
              );
            })}

            {Object.keys(filteredOptions).length === 0 &&
              filteredRegions.length === 0 && (
                <div className='p-4 text-sm text-gray-500'>
                  No options found.
                </div>
              )}
          </Listbox.Options>
        </Transition>
      </div>
    </Listbox>
  );
}

function DropdownHubspotCompany({
  id,
  className = '',
}: {
  id?: string;
  className?: string;
}) {
  const isSuperOwner = checkSuperOwner();
  const dispatch = useAppDispatch();

  const hubspotCompanyIds = useAppSelector(
    (state: RootState) => state.generalFilterSlice.hubspotCompanyIds
  );
  const activeHubspotCompanyId = useAppSelector(
    (state: RootState) => state.generalFilterSlice.activeHubspotCompanyId
  );
  const userId = useAppSelector(
    (state: RootState) => state.users.user?.data?.user?._id
  );
  const [userHubspotIds, setUserHubspotIds] = useState<string[]>([]);
  const [selectedId, setSelectedId] = useState<string>('');

  useEffect(() => {
    if (isSuperOwner) return;
    if (!userId) return;
    const fetchUserHubspotIds = async () => {
      try {
        const res = await AxiosInstance.get(`/hubspot/user/${userId}`);
        const ids: string[] = res.data.data || [];
        setUserHubspotIds(ids);
        if (ids.length > 0) {
          setSelectedId(ids[0]);
          dispatch(setActiveHubspotCompanyId(ids[0]));
        }
      } catch (err) {
        console.error('Failed to fetch user hubspot ids', err);
      }
    };
    fetchUserHubspotIds();
  }, [userId]);

  const displayedIds = isSuperOwner ? hubspotCompanyIds : userHubspotIds;
  const displayedValue = isSuperOwner ? activeHubspotCompanyId : selectedId;

  const { options, loading: optionsLoading } = useHubspotCompanyOptions(
    displayedIds ?? []
  );

  if (!displayedIds?.length) return null;

  return (
    <div className='z-50 flex flex-start flex-col md:flex-row md:items-center gap-3'>
      <div className={`flex flex-col gap-1 text-white ${id && 'w-full'}`}>
        <TomSelect
          id={id ?? 'hubspot-company-filter'}
          value={displayedValue ?? ''}
          name='hubspot-company-filter'
          onChange={(e: { target: { value: string } }) => {
            const value = e.target.value;
            if (!value) return;
            setSelectedId(value);
            dispatch(setActiveHubspotCompanyId(value));
          }}
          className={`z-50 cursor-pointer w-full min-w-[18.5rem] group-[.mode--light]:!border-transparent group-[.mode--light]:!bg-white/[0.12] group-[.mode--light]:bg-chevron-white group-[.mode--light]:!text-[#30F291] rounded-md text-sm ${displayedValue ? '!text-[#30F291]' : 'text-white border-white/50'
            } ${className}`}
          options={{
            placeholder: optionsLoading
              ? 'Loading...'
              : 'Select HubSpot Company...',
            render: {
              option: (data: { text: string }) => {
                const parts = data.text.split('·').map((s) => s.trim());
                const name = parts[0];
                const domain =
                  parts[1] && parts[1] !== 'undefined' ? parts[1] : null;
                const companyId = parts[parts.length - 1];
                const subtitle = domain || name;
                return `
                  <div class="px-3 py-2">
                    <div class="text-sm font-medium leading-tight">${companyId}</div>
                    <div class="mt-0.5">
                      <span class="text-xs text-slate-400">${subtitle}</span>
                    </div>
                  </div>
                `;
              },
              item: (data: { text: string }) => {
                const parts = data.text.split('·').map((s) => s.trim());
                const companyId = parts[parts.length - 1];
                return `<div class="text-sm">${companyId}</div>`;
              },
            },
          }}
        >
          {options.map((company) => (
            <option
              key={company.id}
              value={company.id}
              className='text-primary'
            >
              {company.name} {company.domain ? `· ${company.domain}` : ''} ·{' '}
              {company.id}
            </option>
          ))}
        </TomSelect>
      </div>
    </div>
  );
}

function DropdownCampaign() {
  const client = getClientId();
  const dispatch = useAppDispatch();
  const {
    branch_id: selectedBranch,
    region_id: selectedRegion,
    selectedCampaign,
  } = useAppSelector((state: RootState) => state.generalFilterSlice);
  const [campaign, setCampaignList] = useState<
    | [
      {
        _id: string;
        name: string;
      },
    ]
    | null
  >();

  const [showUpdateCampaign, setShowUpdateCampaign] = useState<boolean>(false);
  const [campaignRules, setCampaignRules] = useState<
    CampaignDayInputType[] | []
  >([]);
  const [campaignName, setCampaignName] = useState<string>('');
  const [agent, setAgent] = useState<AgentSettings | null>(null);
  const [totalTemplates, setTotalTemplates] = useState<Template[]>([]);
  const [refetch, setRefetch] = useState<boolean>(false);

  const templates = useAppSelector((state: RootState) => state.templates.data);

  useEffect(() => {
    const fetchCampaigns = async () => {
      try {
        const response = await AxiosInstance.get(
          `/re-engage/${client}/list/campaign?region=${selectedRegion}&branch=${selectedBranch}`
        );
        if (response.data?.data?.length > 0) {
          dispatch(setCampaign(response.data.data[0]._id));
          setCampaignList(response.data.data);
        } else {
          dispatch(setCampaign(''));
          setCampaignList(null);
        }
      } catch (error) {
        console.log(error);
      }
    };
    fetchCampaigns();
  }, [client, selectedBranch, selectedRegion, refetch]);

  useEffect(() => {
    dispatch(
      fetchTemplates({
        clientId: client as string,
        branchId: selectedBranch as string,
        regionId: selectedRegion as string,
        withNoBranch: true,
      })
    );
  }, [selectedBranch, selectedRegion, client]);

  useEffect(() => {
    const foundTemplates = [
      ...(templates?.emailTemplate ?? []),
      ...(templates?.smsTemplate ?? []),
      ...(templates?.whatsappTemplate ?? []),
      // Viber belongs here too. This flat list is what the automation rows
      // and the preview resolve a rule's stored `template` id against, so
      // omitting it made a Viber rule render "No Template" and its preview
      // fail with "Please select a template." — even though the rule had a
      // template saved and the automation service resolved it correctly.
      ...(templates?.viberTemplate ?? []),
    ];
    setTotalTemplates(foundTemplates);
  }, [templates]);

  useEffect(() => {
    const fetchCampaignById = async () => {
      if (selectedBranch === 'all' || selectedRegion === 'all') return;
      try {
        const response = await AxiosInstance.get(
          `/re-engage/${client}/campaign/${selectedCampaign}/single?client=${client}&branch=${selectedBranch}&region=${selectedRegion}`
        );
        setCampaignRules(response.data.data.campaign);
        setCampaignName(response.data.data.name);
      } catch (error) {
        console.log(error);
      }
    };
    if (selectedCampaign) fetchCampaignById();
  }, [selectedCampaign]);

  useEffect(() => {
    const fetchAgent = async () => {
      try {
        const response = await AxiosInstance.get('/bot', {
          params: {
            organization_id: client,
            branch: selectedBranch,
          },
        });
        setAgent(response.data.data);
      } catch (error) {
        setAgent(null);
      }
    };

    if (selectedBranch && selectedBranch !== 'all') {
      fetchAgent();
    } else {
      setAgent(null);
    }
  }, []);

  const validate = () => {
    if (!campaignName.trim()) {
      toast.show({
        title: '',
        content: 'Campaign name is required.',
        duration: 2000,
        type: 'warning',
      });
      return false;
    }

    if (campaignRules.length === 0) {
      toast.show({
        title: '',
        content: 'At least one campaign rule is required.',
        duration: 2000,
        type: 'warning',
      });
      return false;
    }
    return true;
  };

  const updateCampaign = async () => {
    if (!validate() || !selectedCampaign) return;
    dispatch(
      updateCampaignById({
        client,
        campaignId: selectedCampaign,
        branch: selectedBranch,
        region: selectedRegion,
        payload: {
          name: campaignName,
          campaign: campaignRules,
        },
      })
    );
    setRefetch((prev) => !prev);
    setShowUpdateCampaign(false);
  };

  return (
    <>
      <div className='min-w-[20rem] max-w-[20rem]'>
        {Array.isArray(campaign) && (
          <>
            <div
              className={`mt-2 block flex-col pt-5 sm:flex xl:flex-row xl:items-center`}
            >
              <div className='inline-block sm:mr-5 min-w-60'>
                <div className='flex items-center'>
                  <div className='font-medium'>Select Campaign </div>
                  <div className='ml-2.5 rounded-md border px-2 py-0.5 text-xs'>
                    Required
                  </div>
                </div>
              </div>

              <div>
                <FormSelect
                  id='campaigns-filter'
                  value={selectedCampaign || ''}
                  name='campaigns-list'
                  onChange={(e) => {
                    const value = e.target.value;
                    dispatch(setCampaign(value));
                  }}
                  className='cursor-pointer w-full min-w-[16rem] text-sm border '
                >
                  <option value='' disabled>
                    Select Campaign...
                  </option>{' '}
                  {campaign &&
                    Array.isArray(campaign) &&
                    campaign.length > 0 &&
                    campaign.map((item, index) => (
                      <option
                        key={index}
                        value={item?._id}
                        className='text-primary'
                      >
                        {item?.name}
                      </option>
                    ))}
                </FormSelect>
              </div>

              <Button
                type='button'
                variant='plain'
                className='h-full mt-2 xl:mt-0 ml-2 md:ml-0'
                onClick={() => setShowUpdateCampaign(true)}
              >
                <Lucide icon='Pen' />
              </Button>
            </div>
          </>
        )}
      </div>

      {showUpdateCampaign && (
        <Dialog
          size='xl'
          open={showUpdateCampaign}
          onClose={() => setShowUpdateCampaign(false)}
        >
          <Dialog.Panel className='p-4'>
            <Dialog.Title className='flex flex-col gap-3 text-lg font-bold'>
              Edit Campaign
            </Dialog.Title>
            <Dialog.Description>
              <CampaignRules
                ruleEditOnly={false}
                totalTemplates={totalTemplates}
                agent={agent}
                campaignRules={campaignRules}
                setCampaignRules={setCampaignRules}
                campaignName={campaignName}
                setCampaignName={setCampaignName}
              />
            </Dialog.Description>
            <Dialog.Footer className='flex items-center justify-center gap-4'>
              <Button
                type='button'
                variant='primary'
                onClick={() => setShowUpdateCampaign(false)}
              >
                Close
              </Button>
              <Button
                variant='success'
                type='button'
                onClick={() => updateCampaign()}
              >
                Save
              </Button>
            </Dialog.Footer>
          </Dialog.Panel>
        </Dialog>
      )}
    </>
  );
}

const HubspotCompanyMultiSelect = ({
  userId,
  email,
  role,
}: {
  userId: string;
  email: string;
  role: string;
}) => {
  const availableIds = useAppSelector(
    (state: RootState) => state.generalFilterSlice.hubspotCompanyIds
  );
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);

  const { options, loading: optionsLoading } = useHubspotCompanyOptions(
    availableIds ?? []
  );

  useEffect(() => {
    if (!open) return;

    const fetchUserHubspotIds = async () => {
      setLoading(true);
      try {
        const res = await AxiosInstance.get(`/hubspot/user/${userId}`);
        setSelected(res.data.data || []);
      } catch (err) {
        console.error('Failed to fetch user hubspot ids for the user', err);
      } finally {
        setLoading(false);
      }
    };

    fetchUserHubspotIds();
  }, [open, userId]);

  const toggleId = (id: string) => {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await AxiosInstance.patch(`/client-teams/${userId}/${getClientId()}`, {
        hubspotCompanyIds: selected,
        email,
        role,
      });
      toast.show({
        title: 'Saved',
        content: 'HubSpot company IDs updated',
        type: 'primary',
        duration: 2000,
      });
      setOpen(false);
    } catch (err) {
      toast.show({
        title: 'Error',
        content: 'Failed to update HubSpot company IDs',
        type: 'warning',
        duration: 2000,
      });
    } finally {
      setSaving(false);
    }
  };

  const isLoading = loading || optionsLoading;

  return (
    <Menu>
      <Menu.Button
        as='div'
        onClick={() => setOpen((prev) => !prev)}
        className='cursor-pointer inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-slate-200 text-xs font-medium text-slate-600 hover:bg-slate-50 transition-colors'
      >
        <Lucide icon='Building2' className='w-3.5 h-3.5' />
        HubSpot
        {selected.length > 0 && (
          <span className='ml-1 bg-primary text-white text-[10px] rounded-full px-1.5 py-0.5'>
            {selected.length}
          </span>
        )}
        <Lucide icon='ChevronDown' className='w-3 h-3 ml-0.5' />
      </Menu.Button>

      <Menu.Items className='w-72 p-2' placement='bottom-end'>
        <p className='text-[10px] uppercase tracking-widest text-slate-400 font-semibold px-2 mb-2'>
          Select HubSpot Companies
        </p>

        {isLoading ? (
          <div className='flex justify-center py-4'>
            <Lucide
              icon='Loader'
              className='w-4 h-4 animate-spin text-slate-400'
            />
          </div>
        ) : (
          <div className='flex flex-col gap-0.5 max-h-56 overflow-y-auto mb-2'>
            {options.map((company) => {
              const isSelected = selected.includes(company.id);
              const subtitle = company.domain || company.name;
              return (
                <div
                  key={company.id}
                  onClick={() => toggleId(company.id)}
                  className='flex items-center gap-2.5 px-2 py-2 rounded-md hover:bg-slate-100 cursor-pointer'
                >
                  <div
                    className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 transition-colors ${isSelected
                        ? 'bg-primary border-primary'
                        : 'border-slate-300'
                      }`}
                  >
                    {isSelected && (
                      <Lucide icon='Check' className='w-2.5 h-2.5 text-white' />
                    )}
                  </div>
                  <div className='flex flex-col min-w-0'>
                    <span className='font-mono text-xs text-slate-700 truncate'>
                      {company.id}
                    </span>
                    {subtitle && (
                      <span className='text-[11px] text-slate-400 truncate'>
                        {subtitle}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <div className='border-t pt-2 flex justify-end'>
          <button
            onClick={handleSave}
            disabled={saving || isLoading}
            className='flex items-center gap-1.5 px-3 py-1.5 bg-primary text-white text-xs rounded-md hover:bg-primary/90 disabled:opacity-60 transition-colors'
          >
            {saving ? (
              <Lucide icon='Loader' className='w-3 h-3 animate-spin' />
            ) : (
              <Lucide icon='Save' className='w-3 h-3' />
            )}
            Save
          </button>
        </div>
      </Menu.Items>
    </Menu>
  );
};

interface IActiveCampaign {
  _id: string;
  client: string;
  branch: string;
  region: string | null;
  campaign_id: {
    _id: string;
    name: string;
  };
  csvMedia: string;
  start_date: string;
  end_date: string;
  status: string;
  type: string;
}

interface Props {
  value: string;
  onChange: (id: string) => void;
  onLoadingChange?: (loading: boolean) => void;
}

function DropdownActiveCampaign({ value, onChange, onLoadingChange }: Props) {
  const {
    selectedCampaign,
    branch_id: selectedBranch,
    region_id: selectedRegion,
  } = useAppSelector((state: RootState) => state.generalFilterSlice);

  const [list, setList] = useState<IActiveCampaign[]>([]);
  const [loading, setLoading] = useState(false);

  const isOthersMode = !selectedCampaign && selectedBranch === 'others';
  const client = getClientId();

  useEffect(() => {
    onLoadingChange?.(loading);
  }, [loading]);

  useEffect(() => {
    if (!selectedCampaign && !isOthersMode) {
      setList([]);
      onChange('');
      return;
    }

    const controller = new AbortController();

    const fetchList = async () => {
      try {
        setLoading(true);

        let data: IActiveCampaign[];

        if (isOthersMode) {
          const params: Record<string, string> = {};

          console.log('fetching for all client');
          const res = await AxiosInstance.get(
            `/re-engage/active-campaign/client/${client}/all`,
            { params, signal: controller.signal }
          );
          console.log('res.data.data', res.data.data);
          data = res.data.data ?? [];
        } else {
          const res = await AxiosInstance.get(
            `/re-engage/active-campaign/${selectedCampaign}?client=${client}&branch=${selectedBranch}&region=${selectedRegion}`,
            { signal: controller.signal }
          );
          console.log('res.data.data', res.data.data);
          data = res.data.data ?? [];
        }

        setList(data);
        onChange(data[0]?._id ?? '');
      } catch (err: any) {
        if (err.name === 'CanceledError') return;
        console.log('Error fetching active campaigns', err);
        toast.show({
          title: 'Error',
          content: err.message || 'Failed to fetch active campaigns',
          type: 'warning',
          duration: 2000,
        });
      } finally {
        setLoading(false);
      }
    };

    fetchList();
    return () => controller.abort();
  }, [selectedCampaign, selectedBranch, selectedRegion, isOthersMode]);

  return (
    <div className='mt-4'>
      <div className='inline-block mr-4 min-w-60'>
        <div className='flex items-center'>
          <div className='font-medium'>Select Active Campaign</div>
          <div className='ml-2.5 rounded-md border px-2 py-0.5 text-xs'>
            Required
          </div>
        </div>
      </div>
      <FormSelect
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className='max-w-[16rem] rounded-[0.5rem] text-sm h-9'
        disabled={loading || list.length === 0}
      >
        {list.length === 0 ? (
          <option value=''>
            {loading ? 'Loading…' : 'No active campaigns'}
          </option>
        ) : (
          list.map((ac, idx) => (
            <option key={ac._id} value={ac._id}>
              {ac.type === 'RE_ENGAGE_CSV'
                ? `CSV - ${idx + 1}` +
                (isOthersMode ? ` (${ac.campaign_id?.name})` : '')
                : `${format(parseISO(ac.start_date), 'yyyy-MM-dd')} – ${format(parseISO(ac.end_date), 'yyyy-MM-dd')}`}
            </option>
          ))
        )}
      </FormSelect>
    </div>
  );
}

function DropdownDomain({
  id,
  className = '',
  onChange,
}: {
  id?: string;
  className?: string;
  onChange?: (domain: string) => void;
}) {
  const client = getClientId();
  const [domains, setDomains] = useState<string[]>([]);
  const [selectedDomain, setSelectedDomain] = useState<string>('');

  useEffect(() => {
    const fetchBranches = async () => {
      const response = await AxiosInstance.get(`/branches/get-all/${client}`);
      const branches: { _id: string; name: string; email?: string }[] =
        response.data.data;

      // Extract unique domains from branch emails
      const uniqueDomains = [
        ...new Set(
          branches
            .flatMap((b) => (b.email ?? '').split(','))
            .map((email) => email.trim().split('@')[1])
            .filter(Boolean) as string[]
        ),
      ];

      setDomains(uniqueDomains);

      // select first domain
      if (uniqueDomains.length > 0) {
        setSelectedDomain(uniqueDomains[0]);
        onChange?.(uniqueDomains[0]);
      }
    };

    fetchBranches();
  }, [client]);

  const handleChange = (e: { target: { value: string } }) => {
    const value = e.target.value;
    if (!value) return;
    setSelectedDomain(value);
    onChange?.(value);
  };

  if (!domains.length) return null;

  return (
    <div className='z-50 flex flex-start flex-col md:flex-row md:items-center gap-3'>
      <div className={`flex flex-col gap-1 text-white ${id && 'w-full'}`}>
        <TomSelect
          id={id ?? 'domain-filter'}
          value={selectedDomain}
          name='domain-filter'
          onChange={handleChange}
          className={`z-50 cursor-pointer w-full min-w-[18.5rem] group-[.mode--light]:!border-transparent group-[.mode--light]:!bg-white/[0.12] group-[.mode--light]:bg-chevron-white group-[.mode--light]:!text-[#30F291] rounded-md text-sm ${selectedDomain ? '!text-[#30F291]' : 'text-white border-white/50'
            } ${className}`}
          options={{ placeholder: 'Select Domain...' }}
        >
          {domains.map((domain, index) => (
            <option key={index} value={domain} className='text-primary'>
              {domain}
            </option>
          ))}
        </TomSelect>
      </div>
    </div>
  );
}

interface ClientFilterDropdownProps {
  id?: string;
  value: string;
  onChange: (clientId: string) => void;
  className?: string;
}

function DropdownMetricClient({
  id = 'clients-filter',
  value,
  onChange,
  className,
}: ClientFilterDropdownProps) {
  const [clients, setClients] = useState<IClient[] | null>(null);

  useEffect(() => {
    if (!checkSuperOwner()) {
      setClients([]);
      return;
    }
    const fetchClients = async () => {
      try {
        const response = await AxiosInstance.get(
          `/clients/metric-client?perpage=100`
        );
        if (response.data.success) setClients(response.data.data.data);
      } catch (error) {
        console.log(error);
        setClients([]);
      }
    };
    fetchClients();
  }, []);

  return (
    <div className='z-50 flex flex-start flex-col md:flex-row md:items-center gap-3'>
      {Array.isArray(clients) && clients.length > 0 && (
        <TomSelect
          name='clients-filter'
          id={id}
          value={value}
          className={
            className ??
            'z-50 cursor-pointer min-w-[16rem] capitalize bg-white border-slate-200 [&_.ts-control]:!text-slate-700 [&_.ts-control_input]:!text-slate-700 '
          }
          onChange={(e: { target: { value: string } }) =>
            onChange(e.target.value)
          }
          options={{ allowEmptyOption: true }}
        >
          {clients.map((c) => (
            <option key={c._id} value={c._id}>
              {c.name}
            </option>
          ))}
        </TomSelect>
      )}
    </div>
  );
}

export {
  DropdownActiveCampaign,
  DropdownAssets,
  DropdownBranch,
  DropdownBranchMapping,
  DropdownChannels,
  MemoizedDropdownClients as DropdownClients,
  DropdownClientTeams,
  DropdownDateRange,
  DropdownDateRangeUpdate,
  DropdownDomain,
  DropdownCampaign,
  DropdownHubspotCompany,
  DropdownMultiChannels,
  DropdownRegionAndBranch,
  MemoizedDropdownRegions as DropdownRegions,
  DropdownTeamMembers,
  MemoizedDropdownVenues as DropdownVenues,
  HubspotCompanyMultiSelect,
  DropdownMetricClient,
};
